import { Grid } from '@material-ui/core';
import {
  Card,
  Checkbox,
  i18n,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@nutrien/cxp-components';
import DOMPurify from 'isomorphic-dompurify';
import React, { BaseSyntheticEvent } from 'react';

import { addNewHazardArray } from '@/components/AddWorkFaceAreaInspection/GCHAVerbage';
import GenericLocationComponent from '@/components/GenericLocationComponent';
import { WfaiInfoDetails, WfaiInspectionDetails } from '@/rxdb/Inspections/useInspections';
import { PopulatedLocation } from '@/rxdb/Locations/useLocations';
import useModalStyles from '@/utilities/modalStyles';

import ActiveConditionsCard from '../ActiveConditionsCard';
import RemediatedConditionsCard from '../RemediatedConditionsCard';
import useStyles from './AddWorkFaceAreaInspectionCard.styles';

interface Props {
  onCommentClicked: (id: string) => void;
  inspectionDetails?: WfaiInspectionDetails;
  onAddAnotherHazardClicked: (forceRemediated: boolean) => void;
  onSelectedHazardButtonChange: (value: string) => void;
  onGchaChecked: (checked: boolean) => void;
  disableHazardButtons: boolean;
  wfaiComment: string;
  handleWfaiCommentUpdate: (comment: string) => void;
  wfaiCommentError: string;
  wfaiInfoDetails: WfaiInfoDetails;
  includeLocation: boolean;
  selectedLocation: (location: PopulatedLocation) => void;
  setHasLocationValidationError: (value: boolean) => void;
  onLocationChange: (key: keyof PopulatedLocation, value: any) => void;
}

const AddWorkFaceAreaInspectionCard: React.FC<Props> = ({
  onCommentClicked,
  inspectionDetails,
  onAddAnotherHazardClicked,
  onSelectedHazardButtonChange,
  onGchaChecked,
  disableHazardButtons,
  wfaiComment,
  handleWfaiCommentUpdate,
  wfaiCommentError,
  wfaiInfoDetails,
  includeLocation,
  selectedLocation,
  setHasLocationValidationError,
  onLocationChange,
}: Props) => {
  const classes = useStyles();
  const modalClasses = useModalStyles();

  const onNewHazardClick = (value: string) => {
    const haz = addNewHazardArray.find(x => x.value === value);
    if (haz?.openNewHazard) onAddAnotherHazardClicked(haz.forceRemediation);
    onSelectedHazardButtonChange(value);
  };

  const handleGchaChange = (evt: BaseSyntheticEvent) => {
    onGchaChecked(evt.target.checked);
  };

  return (
    <Grid container className={classes.content}>
      <Grid container className={classes.panelWithoutLeftPadding}>
        <>
          {includeLocation && (
            <Grid item xs={12}>
              <Card className={modalClasses.card}>
                <Typography variant="h3">Location</Typography>
                <GenericLocationComponent
                  location={selectedLocation}
                  setHasValidationErrors={setHasLocationValidationError}
                  onLocationChange={onLocationChange}
                  viewOnly={false}
                />
              </Card>
            </Grid>
          )}
          <Grid item container xs={1} justify="center">
            <Grid item>
              <Checkbox
                checked={inspectionDetails?.wfaiDetails?.signoffCompleted}
                onChange={handleGchaChange}
              />
            </Grid>
          </Grid>
          <Grid item xs={11}>
            <Typography variant="h3">
              {/* need to set the html as the text passed has html tags */}
              <span
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(wfaiInfoDetails?.detail) }}
              />
            </Typography>
          </Grid>
        </>
        <Grid item xs={12} container justify="center" className={classes.wfaiCommentContainer}>
          <Grid item xs={11}>
            <Typography variant="h3">{i18n.t('Comment (optional)')}</Typography>
            <TextField
              multiline
              rows={3}
              onChange={event => {
                handleWfaiCommentUpdate(event.target.value?.replace(/ /g, '\u00a0\u200b') || '');
              }}
              value={wfaiComment}
              error={!!wfaiCommentError}
              errorText={wfaiCommentError}
            />
          </Grid>
        </Grid>
      </Grid>
      <ActiveConditionsCard
        showAddButton={false}
        alternateHeaderText={i18n.t('Active hazards')}
        newGroundHazardIds={inspectionDetails?.wfaiDetails?.newGroundHazardIds}
        overrideAddComment={onCommentClicked}
      />
      <Grid container className={classes.panelWithoutTopMargin}>
        <Typography variant="h3" style={{ marginLeft: 6 }}>
          {i18n.t('Add new hazard')}
        </Typography>
        <Grid item xs={12} className={classes.buttonGroupContainer}>
          <ToggleButtonGroup
            value={inspectionDetails?.wfaiDetails?.newHazardStatus || ''}
            onChange={(evt: BaseSyntheticEvent, value: string) => onNewHazardClick(value)}
            exclusive
          >
            {addNewHazardArray.map(haz => (
              <ToggleButton
                key={`toggle-button${haz.value}`}
                value={haz.value}
                aria-label={haz.value}
                showColorOnDisabled
                disableRipple
                disabled={disableHazardButtons}
              >
                <Typography variant="button" className={classes.noTextTransform}>
                  {haz.value}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>
      </Grid>
      <RemediatedConditionsCard
        newGroundHazardIds={inspectionDetails?.wfaiDetails?.newRemediatedGroundHazardIds || []}
        conditionsAlwaysExpanded
        hideAddPhotoAction
        title={i18n.t('Remediated this check')}
      />
    </Grid>
  );
};

export default AddWorkFaceAreaInspectionCard;
